import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from '../utils/toast';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  ChartBarIcon,
  Squares2X2Icon,
  PlusIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const ClientDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = useAuth();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Data states for each tab
  const [tasks, setTasks] = useState([]);
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [customPageSubmissions, setCustomPageSubmissions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [pendingData, setPendingData] = useState([]);
  const [completedData, setCompletedData] = useState([]);
  const [clientBlocks, setClientBlocks] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [timeLogsSummary, setTimeLogsSummary] = useState(null);

  // Loading states for each tab
  const [tasksLoading, setTasksLoading] = useState(false);
  const [formsLoading, setFormsLoading] = useState(false);
  const [customPagesLoading, setCustomPagesLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [timeLogsLoading, setTimeLogsLoading] = useState(false);

  // Blocks modal states
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [availableBlocks, setAvailableBlocks] = useState([]);
  const [selectedBlockTemplate, setSelectedBlockTemplate] = useState('');
  const [blockInstanceName, setBlockInstanceName] = useState('');
  const [blockFieldData, setBlockFieldData] = useState({});
  const [editingBlockInstance, setEditingBlockInstance] = useState(null);

  const canViewTasks = checkPermission('View Tasks');
  const canViewForms = checkPermission('View Forms and Tasks');
  const canViewCustomPages = checkPermission('View Custom Page Submissions');
  const canViewTimeLogs = checkPermission('View Time Logs');

  useEffect(() => {
    fetchClientDetails();
  }, [clientId]);

  useEffect(() => {
    if (client) {
      fetchTabData();
    }
  }, [client, activeTab, searchTerm, statusFilter]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/master-data/${clientId}`);
      setClient(response.data.data);
    } catch (error) {
      console.error('Error fetching client details:', error);
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async () => {
    switch (activeTab) {
      case 'tasks':
        await fetchTasks();
        break;
      case 'forms':
        await fetchFormSubmissions();
        break;
      case 'custom-pages':
        await fetchCustomPageSubmissions();
        break;
      case 'logs':
        await fetchLogs();
        break;
      case 'time-logs':
        await fetchTimeLogs();
        break;
      case 'pending':
        await fetchPendingData();
        break;
      case 'completed':
        await fetchCompletedData();
        break;
      case 'blocks':
        await fetchClientBlocks();
        break;
    }
  };

  const fetchTasks = async () => {
    try {
      setTasksLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        status: statusFilter
      });
      const response = await api.get(`/client/${clientId}/tasks?${params}`);
      setTasks(response.data.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchFormSubmissions = async () => {
    try {
      setFormsLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        status: statusFilter
      });
      const response = await api.get(`/client/${clientId}/form-submissions?${params}`);
      setFormSubmissions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching form submissions:', error);
      toast.error('Failed to load form submissions');
    } finally {
      setFormsLoading(false);
    }
  };

  const fetchCustomPageSubmissions = async () => {
    try {
      setCustomPagesLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        status: statusFilter
      });
      const response = await api.get(`/client/${clientId}/custom-page-submissions?${params}`);
      setCustomPageSubmissions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching custom page submissions:', error);
      toast.error('Failed to load custom page submissions');
    } finally {
      setCustomPagesLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const params = new URLSearchParams({
        search: searchTerm
      });
      const response = await api.get(`/client/${clientId}/logs?${params}`);
      setLogs(response.data.data || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchTimeLogs = async () => {
    try {
      setTimeLogsLoading(true);
      const response = await api.get(`/time-logs/client/${clientId}`);
      setTimeLogs(response.data.data || []);
      setTimeLogsSummary(response.data.summary || null);
    } catch (error) {
      console.error('Error fetching time logs:', error);
      toast.error('Failed to load time logs');
    } finally {
      setTimeLogsLoading(false);
    }
  };

  const fetchPendingData = async () => {
    try {
      setPendingLoading(true);
      const params = new URLSearchParams({
        search: searchTerm
      });
      
      const response = await api.get(`/client/${clientId}/pending?${params}`);
      setPendingData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching pending data:', error);
      toast.error('Failed to load pending data');
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchCompletedData = async () => {
    try {
      setCompletedLoading(true);
      const params = new URLSearchParams({
        search: searchTerm
      });
      
      const response = await api.get(`/client/${clientId}/completed?${params}`);
      setCompletedData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching completed data:', error);
      toast.error('Failed to load completed data');
    } finally {
      setCompletedLoading(false);
    }
  };

  const fetchClientBlocks = async () => {
    try {
      setBlocksLoading(true);
      const response = await api.get(`/client-blocks/master-data/${clientId}`);
      setClientBlocks(response.data.data || []);
    } catch (error) {
      console.error('Error fetching client blocks:', error);
      toast.error('Failed to load blocks');
    } finally {
      setBlocksLoading(false);
    }
  };

  const fetchAvailableBlocks = async () => {
    try {
      const response = await api.get('/blocks');
      if (response.data.success) {
        setAvailableBlocks(response.data.data.filter(b => b.isActive));
      }
    } catch (error) {
      console.error('Error fetching available blocks:', error);
      toast.error('Failed to load block templates');
    }
  };

  const handleOpenBlockModal = async (blockInstance = null) => {
    await fetchAvailableBlocks();
    
    if (blockInstance) {
      setEditingBlockInstance(blockInstance);
      setSelectedBlockTemplate(blockInstance.blockId._id);
      setBlockInstanceName(blockInstance.blockName);
      setBlockFieldData(blockInstance.fieldData || {});
    } else {
      setEditingBlockInstance(null);
      setSelectedBlockTemplate('');
      setBlockInstanceName('');
      setBlockFieldData({});
    }
    
    setShowBlockModal(true);
  };

  const handleCloseBlockModal = () => {
    setShowBlockModal(false);
    setEditingBlockInstance(null);
    setSelectedBlockTemplate('');
    setBlockInstanceName('');
    setBlockFieldData({});
  };

  const handleBlockTemplateChange = (blockId) => {
    setSelectedBlockTemplate(blockId);
    const block = availableBlocks.find(b => b._id === blockId);
    if (block) {
      const initialFieldData = {};
      block.fields.forEach(field => {
        initialFieldData[field.fieldName] = '';
      });
      setBlockFieldData(initialFieldData);
    }
  };

  const handleBlockFieldChange = (fieldName, value) => {
    setBlockFieldData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSaveBlockInstance = async (e) => {
    e.preventDefault();
    
    if (!selectedBlockTemplate) {
      toast.error('Please select a block template');
      return;
    }
    
    if (!blockInstanceName.trim()) {
      toast.error('Please enter a block name');
      return;
    }

    try {
      const dataToSubmit = {
        masterDataId: clientId,
        blockId: selectedBlockTemplate,
        blockName: blockInstanceName,
        fieldData: blockFieldData
      };

      if (editingBlockInstance) {
        await api.put(`/client-blocks/${editingBlockInstance._id}`, {
          blockName: blockInstanceName,
          fieldData: blockFieldData
        });
        toast.success('Block updated successfully');
      } else {
        await api.post('/client-blocks', dataToSubmit);
        toast.success('Block created successfully');
      }
      
      handleCloseBlockModal();
      fetchClientBlocks();
    } catch (error) {
      console.error('Error saving block:', error);
      toast.error(error.response?.data?.message || 'Failed to save block');
    }
  };

  const handleDeleteBlockInstance = async (blockInstanceId) => {
    if (!window.confirm('Are you sure you want to delete this block?')) {
      return;
    }

    try {
      await api.delete(`/client-blocks/${blockInstanceId}`);
      toast.success('Block deleted successfully');
      fetchClientBlocks();
    } catch (error) {
      console.error('Error deleting block:', error);
      toast.error('Failed to delete block');
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'task':
        return <ClipboardDocumentListIcon className="h-5 w-5 text-blue-500" />;
      case 'form':
        return <DocumentTextIcon className="h-5 w-5 text-green-500" />;
      case 'custom-page':
        return <UserGroupIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <ChartBarIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderTasks = () => (
    <div className="space-y-4">
      {tasksLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No tasks found for this client</div>
      ) : (
        <div className="grid gap-4">
          {tasks.map((task) => (
            <div key={task._id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(task.status)}
                  <div>
                    <h3 className="font-medium text-gray-900">{task.name}</h3>
                    <p className="text-sm text-gray-500">{task.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(task.createdAt)}</div>
                  <div className="text-xs text-gray-400">Deadline: {formatDate(task.deadline)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderFormSubmissions = () => (
    <div className="space-y-4">
      {formsLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : formSubmissions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No form submissions found for this client</div>
      ) : (
        <div className="grid gap-4">
          {formSubmissions.map((submission) => (
            <div key={submission._id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(submission.status)}
                  <div>
                    <h3 className="font-medium text-gray-900">{submission.formName || 'Form Submission'}</h3>
                    <p className="text-sm text-gray-500">
                      Submitted by: {submission.userEmail}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(submission.createdAt)}</div>
                  <div className="text-xs text-gray-400">
                    Total Fees: ${submission.totalFees?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCustomPageSubmissions = () => (
    <div className="space-y-4">
      {customPagesLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : customPageSubmissions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No custom page submissions found for this client</div>
      ) : (
        <div className="grid gap-4">
          {customPageSubmissions.map((submission) => (
            <div key={submission._id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(submission.status)}
                  <div>
                    <h3 className="font-medium text-gray-900">{submission.name}</h3>
                    <p className="text-sm text-gray-500">
                      Page: {submission.customPage?.name || 'Unknown Page'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(submission.createdAt)}</div>
                  <div className="text-xs text-gray-400">
                    Submitted by: {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderLogs = () => (
    <div className="space-y-4">
      {logsLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No logs found for this client</div>
      ) : (
        <div className="grid gap-4">
          {logs.map((log) => (
            <div key={log._id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div>
                    <h3 className="font-medium text-gray-900">{log.actionType}</h3>
                    <p className="text-sm text-gray-500">{log.details}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(log.metadata?.timestamp)}</div>
                  <div className="text-xs text-gray-400">
                    By: {log.userId?.firstName} {log.userId?.lastName}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPendingData = () => (
    <div className="space-y-4">
      {pendingLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : pendingData.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No pending items found for this client</div>
      ) : (
        <div className="grid gap-4">
          {pendingData.map((item) => (
            <div key={`${item.type}-${item._id}`} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getTypeIcon(item.type)}
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {item.name || item.formName || 'Unknown Item'}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize">{item.type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(item.createdAt)}</div>
                  <div className="text-xs text-gray-400">Status: {item.status}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCompletedData = () => (
    <div className="space-y-4">
      {completedLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : completedData.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No completed items found for this client</div>
      ) : (
        <div className="grid gap-4">
          {completedData.map((item) => (
            <div key={`${item.type}-${item._id}`} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getTypeIcon(item.type)}
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {item.name || item.formName || 'Unknown Item'}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize">{item.type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(item.updatedAt || item.createdAt)}</div>
                  <div className="text-xs text-gray-400">Completed: {formatDate(item.completedAt || item.updatedAt)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderBlocks = () => (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Client Blocks</h3>
        {checkPermission('Update Master Data') && (
          <button
            onClick={() => handleOpenBlockModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Block
          </button>
        )}
      </div>

      {blocksLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading blocks...</p>
        </div>
      ) : clientBlocks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Squares2X2Icon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No blocks added yet</p>
          {checkPermission('Update Master Data') && (
            <button
              onClick={() => handleOpenBlockModal()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Your First Block
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {clientBlocks.map((blockInstance) => (
            <div key={blockInstance._id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="text-md font-semibold text-gray-900">{blockInstance.blockName}</h4>
                  <p className="text-sm text-gray-500">Template: {blockInstance.blockId?.name}</p>
                </div>
                {checkPermission('Update Master Data') && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleOpenBlockModal(blockInstance)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteBlockInstance(blockInstance._id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {blockInstance.blockId?.fields.map((field, idx) => {
                  const fieldValue = blockInstance.fieldData?.[field.fieldName] || '-';
                  return (
                    <div key={idx} className="text-sm">
                      <span className="font-medium text-gray-700">{field.fieldName}:</span>{' '}
                      <span className="text-gray-600">{fieldValue}</span>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                Created by {blockInstance.createdBy?.firstName} {blockInstance.createdBy?.lastName} on{' '}
                {new Date(blockInstance.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderTimeLogs = () => (
    <div className="space-y-4">
      {/* Add Time Log Button */}
      <div className="flex justify-end">
        <button
          onClick={() => navigate('/new/time-log')}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Time Log
        </button>
      </div>

      {timeLogsLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading time logs...</p>
        </div>
      ) : timeLogs.length === 0 ? (
        <div className="text-center py-8">
          <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 mb-4">No time logs found for this client</p>
          <button
            onClick={() => navigate('/new/time-log')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add First Time Log
          </button>
        </div>
      ) : (
        <>
          {/* Summary Card */}
          {timeLogsSummary && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">Summary</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-blue-700">Total Time</p>
                  <p className="text-lg font-bold text-blue-900">{timeLogsSummary.totalTime}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-700">Total Amount</p>
                  <p className="text-lg font-bold text-blue-900">${timeLogsSummary.totalAmount?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-700">Total Entries</p>
                  <p className="text-lg font-bold text-blue-900">{timeLogs.length}</p>
                </div>
              </div>
            </div>
          )}

          {/* Time Logs Table */}
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate/Hr
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attachments
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created By
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {timeLogs.map((timeLog) => (
                    <tr key={timeLog._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(timeLog.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                        <div className="line-clamp-2" title={timeLog.description}>
                          {timeLog.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${timeLog.ratePerHour}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {timeLog.hours}h {timeLog.minutes}m
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                        ${timeLog.totalAmount?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {timeLog.attachments && timeLog.attachments.length > 0 ? (
                          <div className="space-y-1">
                            {timeLog.attachments.map((attachment, idx) => (
                              <a
                                key={idx}
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                                title={attachment.filename}
                              >
                                <DocumentTextIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                <span className="truncate max-w-[150px]">{attachment.filename}</span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">
                            {timeLog.createdBy?.firstName} {timeLog.createdBy?.lastName}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(timeLog.createdAt).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const tabs = [
    { id: 'tasks', name: 'Tasks', icon: ClipboardDocumentListIcon, count: tasks.length },
    { id: 'forms', name: 'Forms', icon: DocumentTextIcon, count: formSubmissions.length },
    { id: 'custom-pages', name: 'Custom Pages', icon: UserGroupIcon, count: customPageSubmissions.length },
    { id: 'logs', name: 'Logs', icon: ChartBarIcon, count: logs.length },
    { id: 'time-logs', name: 'Time Logs', icon: ClockIcon, count: timeLogs.length },
    { id: 'pending', name: 'Pending', icon: ClockIcon, count: pendingData.length },
    { id: 'completed', name: 'Completed', icon: CheckCircleIcon, count: completedData.length },
    { id: 'blocks', name: 'Blocks', icon: Squares2X2Icon, count: clientBlocks.length }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Client not found</h2>
          <button
            onClick={() => navigate('/master-data')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Master Data
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/master-data')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Master Data
          </button>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{client.clientName}</h1>
            <p className="text-gray-600 mb-4">{client.clientAddress}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Email:</span>
                <span className="ml-2 text-gray-600">{client.clientEmail || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Phone:</span>
                <span className="ml-2 text-gray-600">{client.clientPhone || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Location:</span>
                <span className="ml-2 text-gray-600">
                  {[client.clientCity, client.clientState, client.clientCountry].filter(Boolean).join(', ') || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.name}</span>
                    {tab.count > 0 && (
                      <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'tasks' && renderTasks()}
            {activeTab === 'forms' && renderFormSubmissions()}
            {activeTab === 'custom-pages' && renderCustomPageSubmissions()}
            {activeTab === 'logs' && renderLogs()}
            {activeTab === 'time-logs' && renderTimeLogs()}
            {activeTab === 'pending' && renderPendingData()}
            {activeTab === 'completed' && renderCompletedData()}
            {activeTab === 'blocks' && renderBlocks()}
          </div>
        </div>
      </div>

      {/* Block Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingBlockInstance ? 'Edit Block' : 'Add New Block'}
            </h3>
            
            <form onSubmit={handleSaveBlockInstance}>
              <div className="space-y-4">
                {/* Block Template Selection */}
                {!editingBlockInstance && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Block Template *
                    </label>
                    <select
                      value={selectedBlockTemplate}
                      onChange={(e) => handleBlockTemplateChange(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Choose a block template...</option>
                      {availableBlocks.map(block => (
                        <option key={block._id} value={block._id}>
                          {block.name} ({block.fields.length} fields)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Block Instance Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Block Name *
                  </label>
                  <input
                    type="text"
                    value={blockInstanceName}
                    onChange={(e) => setBlockInstanceName(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter a name for this block instance"
                  />
                </div>

                {/* Block Fields */}
                {selectedBlockTemplate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Block Fields
                    </label>
                    <div className="space-y-3 max-h-96 overflow-y-auto p-4 bg-gray-50 rounded-md">
                      {availableBlocks.find(b => b._id === selectedBlockTemplate)?.fields.map((field, index) => (
                        <div key={index}>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {field.fieldName} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          {field.inputType === 'textarea' ? (
                            <textarea
                              value={blockFieldData[field.fieldName] || ''}
                              onChange={(e) => handleBlockFieldChange(field.fieldName, e.target.value)}
                              required={field.required}
                              placeholder={field.placeholder}
                              rows="3"
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : field.inputType === 'select' ? (
                            <select
                              value={blockFieldData[field.fieldName] || ''}
                              onChange={(e) => handleBlockFieldChange(field.fieldName, e.target.value)}
                              required={field.required}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select {field.fieldName}...</option>
                              {field.options?.map((option, idx) => (
                                <option key={idx} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : field.inputType === 'checkbox' ? (
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={blockFieldData[field.fieldName] === 'true' || blockFieldData[field.fieldName] === true}
                                onChange={(e) => handleBlockFieldChange(field.fieldName, e.target.checked.toString())}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </div>
                          ) : (
                            <input
                              type={field.inputType}
                              value={blockFieldData[field.fieldName] || ''}
                              onChange={(e) => handleBlockFieldChange(field.fieldName, e.target.value)}
                              required={field.required}
                              placeholder={field.placeholder}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseBlockModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  {editingBlockInstance ? 'Update Block' : 'Create Block'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetails; 